import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    TextField,
    Grid,
    Typography,
    Card,
    Divider,
    InputLabel,
    Alert,
    Snackbar,
    useMediaQuery,
    CircularProgress,
} from "@mui/material";
import Sidenav from "../../common/Sidenav";
import Header from "../../common/Header";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { DropzoneArea } from "mui-file-dropzone";
import { config } from "../../../config/config";
import { apiList, invokeApi } from "../../../services/apiServices";
import { useCookies } from "react-cookie";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import IconSidenav from "../../common/IconSidenav";
import NavigatedComponent from "../NavigatedComponent";
import DriveFolderUploadIcon from "@mui/icons-material/DriveFolderUpload";

const EditFilm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const filmDataFromState = location.state?.filmData;

    const [filmDetails, setFilmDetails] = useState({
        title: "",
        url: "",
        imageUrl: "",
    });
    const [cookies] = useCookies();
    const [filmImage, setFilmImage] = useState(null);
    const [selectedFilmImage, setSelectedFilmImage] = useState("");
    const [imageChanged, setImageChanged] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("");
    const [showSideNav, setShowSideNav] = useState(false);
    const isMobileScreen = useMediaQuery(
        "(min-width:360px) and (max-width:500px)"
    );
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(!filmDataFromState);

    const toggleSideNav = () => {
        setShowSideNav(!showSideNav);
    };

    useEffect(() => {
        const fetchFilmData = async () => {
            if (!filmDataFromState && id) {
                try {
                    setFetching(true);
                    const params = { id: id };
                    const response = await invokeApi(
                        config.getMyCollege + apiList.getFilm,
                        params,
                        cookies
                    );

                    if (response?.status === 200) {
                        const filmData = response.data.film || response.data.data;
                        setFilmDetails({
                            title: filmData.title || filmData.name || "",
                            url: filmData.url || "",
                            imageUrl: filmData.imageUrl || filmData.image || "",
                        });
                        setSelectedFilmImage(filmData.imageUrl || filmData.image || "");
                    } else {
                        showSnackbar("Failed to fetch film data", "error");
                        navigate("/films-manager/films-list");
                    }
                } catch (error) {
                    console.error("Error fetching film data:", error);
                    showSnackbar("Error fetching film data", "error");
                    navigate("/films-manager/films-list");
                } finally {
                    setFetching(false);
                }
            } else if (filmDataFromState) {
                setFilmDetails({
                    title: filmDataFromState.title || filmDataFromState.name || "",
                    url: filmDataFromState.url || "",
                    imageUrl: filmDataFromState.imageUrl || filmDataFromState.image || "",
                });
                setSelectedFilmImage(filmDataFromState.imageUrl || filmDataFromState.image || "");
            }
        };

        fetchFilmData();
    }, [id, filmDataFromState, cookies, navigate]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFilmDetails((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleFilmImage = (files) => {
        if (files && files.length > 0) {
            setImageChanged(true);
            const selectedFile = files[0];
            setFilmImage(selectedFile);

            const reader = new FileReader();
            reader.onload = () => {
                setSelectedFilmImage(reader.result);
            };
            reader.readAsDataURL(selectedFile);
        } else {
            setImageChanged(false);
            setFilmImage(null);
        }
    };

    const showSnackbar = (message, severity) => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
    };

    const uploadFilmImage = async () => {
        if (!filmImage) {
            return filmDetails.imageUrl;
        }

        const formData = new FormData();
        formData.append("image", filmImage);
        try {
            const response = await fetch("https://api.reef.org.in/uploadFilmImage", {
                method: "POST",
                body: formData,
            });

            if (response.ok) {
                const data = await response.json();
                return data.imageUrl || "";
            } else {
                showSnackbar("Failed to upload image", "error");
                return filmDetails.imageUrl;
            }
        } catch (error) {
            showSnackbar("Error uploading image", "error");
            return filmDetails.imageUrl;
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!filmDetails.title || !filmDetails.url) {
            showSnackbar("Title and URL are required", "error");
            return;
        }

        try {
            setLoading(true);
            const imageUrl = await uploadFilmImage();

            const updatedFilmData = {
                id: id,
                title: filmDetails.title,
                url: filmDetails.url,
                imageUrl: imageUrl,
                image: imageUrl, // For compatibility
            };

            const response = await invokeApi(
                config.getMyCollege + apiList.updateFilm,
                updatedFilmData,
                cookies
            );

            if (response?.status >= 200 && response?.status < 300) {
                showSnackbar("Film updated successfully", "success");
                setTimeout(() => {
                    navigate("/films-manager/films-list");
                }, 1000);
            } else {
                showSnackbar(response.data?.message || "Failed to update film", "error");
            }
        } catch (error) {
            console.error("Error updating film:", error);
            showSnackbar("Something went wrong", "error");
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return (
            <Grid sx={{ display: "flex" }}>
                {isMobileScreen ? <IconSidenav /> : showSideNav ? <IconSidenav /> : <Sidenav />}
                <Grid component="main" sx={{ width: "100%", px: 1 }}>
                    <Header toggleSideNav={toggleSideNav} />
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
                        <CircularProgress />
                    </Box>
                </Grid>
            </Grid>
        );
    }

    return (
        <div>
            <Grid sx={{ display: "flex" }}>
                {isMobileScreen ? <IconSidenav /> : showSideNav ? <IconSidenav /> : <Sidenav />}
                <Grid component="main" sx={{ width: "100%", px: 1 }}>
                    <Header toggleSideNav={toggleSideNav} />

                    <Grid sx={{ py: 1 }}>
                        <NavigatedComponent pathname={location.pathname} />
                        <Grid sx={{ width: "100%", px: isMobileScreen ? 1 : 5 }}>
                            <Card elevation={1} sx={{ my: 5 }}>
                                <Grid sx={{ py: 2 }}>
                                    <Typography
                                        sx={{
                                            mb: 2,
                                            mx: isMobileScreen ? 2 : 8,
                                            fontSize: isMobileScreen ? 14 : 18,
                                            fontWeight: 600,
                                            textTransform: "uppercase",
                                        }}
                                    >
                                        Edit Film
                                    </Typography>
                                    <Divider sx={{ mb: 3 }} />

                                    <form onSubmit={handleSubmit}>
                                        <Grid container sx={{ justifyContent: "center" }}>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    Film Title *
                                                </InputLabel>
                                                <TextField
                                                    fullWidth
                                                    name="title"
                                                    value={filmDetails.title}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    Video URL *
                                                </InputLabel>
                                                <TextField
                                                    fullWidth
                                                    name="url"
                                                    value={filmDetails.url}
                                                    onChange={handleInputChange}
                                                />
                                            </Grid>

                                            <Grid item md={11} xs={11} sx={{ my: 2 }}>
                                                <InputLabel sx={{ fontWeight: "bold", my: 1, fontSize: 15 }}>
                                                    Poster Image
                                                </InputLabel>
                                                <DropzoneArea
                                                    filesLimit={1}
                                                    onChange={handleFilmImage}
                                                    acceptedFiles={["image/jpeg", "image/png"]}
                                                    maxFileSize={5000000}
                                                    showPreviews={true}
                                                    showPreviewsInDropzone={false}
                                                    Icon={() => <CloudUploadIcon sx={{ color: "#1d1a57", width: 35, height: 35 }} />}
                                                    dropzoneText={
                                                        <Typography sx={{ color: "#1d1a57", fontWeight: 600 }}>
                                                            Upload / Drag new image to replace
                                                        </Typography>
                                                    }
                                                />
                                                {selectedFilmImage && (
                                                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                                                        <img src={selectedFilmImage} alt="Current Poster" style={{ maxWidth: "150px" }} />
                                                    </Box>
                                                )}
                                            </Grid>

                                        </Grid>

                                        <Divider sx={{ mt: 4 }} />
                                        <Grid container sx={{ justifyContent: "center" }}>
                                            <Button
                                                type="submit"
                                                disabled={loading}
                                                sx={{
                                                    my: 2,
                                                    px: 3,
                                                    background: "#1d1a57",
                                                    color: "#FFF",
                                                    "&:hover": { background: "#1d1a57" },
                                                }}
                                            >
                                                <DriveFolderUploadIcon /> &nbsp;
                                                {loading ? "Updating..." : "Save Changes"}
                                            </Button>
                                        </Grid>
                                    </form>
                                </Grid>
                            </Card>
                            <Snackbar
                                open={openSnackbar}
                                autoHideDuration={4000}
                                onClose={handleCloseSnackbar}
                                anchorOrigin={{ vertical: "top", horizontal: "center" }}
                            >
                                <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity}>
                                    {snackbarMessage}
                                </Alert>
                            </Snackbar>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};
export default EditFilm;
